import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import RichText from "components/rich-text";
import ProductItem from "components/product/item";
import Image from "components/image";
import { filterCollectionProducts, searchProducts } from "slices/filters";
import { trackViewItemList } from "services/analytics";
import { useInView } from "react-intersection-observer";
import useModal from "hooks/useModal";
import CollectionSlider from "./slider";
import { SwiperSlide } from "components/swiper";
import AddAllToCartButton from "components/add-all-to-cart";
import { useGlobalContext } from "context/global";
import clsx from "clsx/lite";
import { isMobile } from "utils/device-detect";

const CollectionModule = ({ data }) => {
	const { collection, isSlider = false, showAddAllToCart = false, excludeFromFilters = false } = data ?? {};
	const { products = [], content = [], handle } = collection ?? {};
	const [collectionProducts, setCollectionProducts] = useState(products);
	const { tags, search, bundles } = useSelector((state) => state.filters);
	const { isCollection } = useGlobalContext();

	const dispatch = useDispatch();
	const [collectionRef, collectionInView] = useInView({
		threshold: 0,
		rootMargin: "50px",
		triggerOnce: true,
	});

	useEffect(() => {
		// track view_item_list
		if (collectionInView) {
			trackViewItemList(collection);
		}
	}, [collectionInView]);

	useEffect(() => {
		const productsResult = dispatch(
			search
				? searchProducts(search.toLowerCase(), collection)
				: filterCollectionProducts(collection)
		);
		setCollectionProducts(productsResult);
	}, [search, tags, bundles, collection]);

	// Don't render the collection if excludeFromFilters is true and there are active filters
	const isAnyFilterActive = useMemo(() => Object.values(tags).some(tag => tag.isSettled)
		|| Object.values(bundles).some(bundle => bundle.isSettled) || search, [tags, bundles, search]);

	const genKey = useCallback((id) => `${handle}-${id}`, [handle]);

	if (excludeFromFilters && isAnyFilterActive) return null;

	if (!collectionProducts?.length) return <div id={handle}></div>;

	return (
		<div
			id={handle}
			ref={collectionRef}
			className={clsx(
				"hd-screen-max-width collection-products",
				isSlider ? "my-8" : "flex flex-col items-center w-full mx-auto my-6"
			)}
		>
			<div className="m-auto text-center w-full md:w-1/2 mb-12">
				<RichText value={content} />
			</div>

			{showAddAllToCart && (
				<div className="hidden md:flex justify-end w-full mb-3 pr-3">
					<AddAllToCartButton products={collectionProducts} className="hidden md:block" />
				</div>
			)}

			{isSlider ? (
				<CollectionSlider amount={data.amount} className={clsx(isCollection && "w-full md:w-[95%] lg:w-11/12 mx-auto")}>
					{collectionProducts?.map((item, index) => (
						<SwiperSlide key={genKey(item.id)}>
							<CollectionItem item={item} index={index} />
						</SwiperSlide>
					))}
				</CollectionSlider>
			) : (
				<div className="flex flex-wrap justify-center">
					{collectionProducts?.map((item, index) => (
						<div id={item.id} key={genKey(item.id)}
							className="w-full mx-[1%] md:w-[30%] lg:w-[22%] mb-10 lg:mx-2">
							<CollectionItem item={item} index={index} />
						</div>
					))}
				</div>
			)}

			{showAddAllToCart && (
				<div className="md:hidden mt-5">
					<AddAllToCartButton products={products} className="block md:hidden mx-auto px-2" />
				</div>
			)}
		</div>
	);
};

const CollectionItem = ({ item, index }) => {
	const [modal, openModal, closeModal] = useModal("product");

	const isMobileView = isMobile();
	return item?._type === "media" ? (
		<Image image={item.mobileImage && isMobileView ? item.mobileImage : item} to={item.url} className="rounded-5" />
	) : (
		<ProductItem
			product={{ ...item, index }}
			isOpen={modal[item._id]}
			openModal={() => openModal(item._id)}
			closeModal={() => closeModal(item._id)}
			newLayout
		/>
	);
};

export default CollectionModule;